import { create } from '@thoughtspot/logger';
import { getColumnIds, isMeasureName, isMeasureValues, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getCustomChartContext } from '../context/custom-chart-app-context';
import { AnswerColumnEffectiveType, } from '../types/prism';
import { isMeasureNameColumn, isMeasureValueColumn } from './common-util';
const logger = create('custom-chart-model-utils');
export const extractColumnIdsFromDimension = (dimension) => {
    const columnIds = [];
    if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
        dimension.axes?.forEach((configItem) => {
            if (configItem.__typename === 'MergedAxis') {
                columnIds.push(...configItem.columns);
            }
            else if (configItem.__typename === 'DualAxis') {
                if (configItem.primary.__typename === 'FlatAxis') {
                    columnIds.push(configItem.primary.column);
                }
                else if (configItem.primary.__typename === 'MergedAxis') {
                    columnIds.push(...configItem.primary.columns);
                }
                if (configItem.secondary.__typename === 'FlatAxis') {
                    columnIds.push(configItem.secondary.column);
                }
                else if (configItem.secondary.__typename === 'MergedAxis') {
                    columnIds.push(...configItem.secondary.columns);
                }
            }
            else {
                configItem.column && columnIds.push(configItem.column);
            }
        });
    }
    else {
        columnIds.push(...(dimension.columns ?? []));
    }
    return columnIds;
};
export const getColumnsfromCustomChartAxisConfig = (axisConfig) => {
    if (axisConfig.__typename === 'MergedAxis') {
        return axisConfig.columns;
    }
    if (axisConfig.__typename === 'DualAxis') {
        return [
            ...getColumnsfromCustomChartAxisConfig(axisConfig.primary),
            ...getColumnsfromCustomChartAxisConfig(axisConfig.secondary),
        ];
    }
    return axisConfig.column ? [axisConfig.column] : [];
};
export const getColumnsfromCustomChartDimension = (dimension) => {
    if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
        return (dimension.axes
            ?.map(axis => {
            return getColumnsfromCustomChartAxisConfig(axis);
        })
            ?.flat() ?? []);
    }
    return dimension.columns ?? [];
};
export const getCustomChartDimensionColumns = (customChartConfig, dim) => {
    let customChartAxisColumns = [];
    customChartConfig.forEach(config => {
        if (config.dimensions && Array.isArray(config.dimensions)) {
            config.dimensions.forEach(dimension => {
                if (dimension.key === dim) {
                    const columns = getColumnsfromCustomChartDimension(dimension);
                    customChartAxisColumns = customChartAxisColumns.concat(columns);
                }
            });
        }
    });
    return customChartAxisColumns;
};
export const getVisualizedColumnsForCustomChart = (chartModel) => {
    const orderedColumnIds = getColumnsFromCustomChartConfig(chartModel.config.customChartConfig);
    const columnById = new Map(chartModel.columns.map(c => [c.column.id, c.column]));
    return Array.from(new Set(orderedColumnIds))
        .map(id => columnById.get(id))
        .filter((col) => col != null && !isMeasureName(col) && !isMeasureValues(col));
};
export const isVisualizedColumnInCustomChart = (column, chartModel) => {
    return getVisualizedColumnsForCustomChart(chartModel).some(col => col.id === column.id);
};
export const getCustomChartConfiguration = () => {
    const { allowedConfigurations } = getCustomChartContext();
    return allowedConfigurations;
};
export const getCustomChartConfigQueries = () => {
    const { chartConfigQueries } = getCustomChartContext();
    return chartConfigQueries;
};
export const isValidAdvancedPivotConfig = (axisConfig) => {
    const hasAttributesInValues = axisConfig.y?.some(col => col.type === AnswerColumnEffectiveType.Attribute);
    return !hasAttributesInValues;
};
export const transformAxisConfigToCustomChartConfig = (axisConfig, allColumns, visualizedColumnIds, customToAxisConfigKeyMapping) => {
    const measureNameColumn = allColumns.find(isMeasureNameColumn);
    const measureNameColumnId = measureNameColumn?.id || null;
    const defaultMeasureNameColumnId = measureNameColumnId &&
        !visualizedColumnIds.includes(measureNameColumnId)
        ? measureNameColumnId
        : '';
    if (!isValidAdvancedPivotConfig(axisConfig)) {
        return [];
    }
    const dimensions = customToAxisConfigKeyMapping.map(([customKey, axisKey]) => {
        const columnsForAxis = axisConfig[axisKey] || [];
        let columnIds = getColumnIds(columnsForAxis);
        if (!measureNameColumnId &&
            axisKey === 'y' &&
            columnIds.length > 0) {
            columnIds = [columnIds[0]];
        }
        if (axisKey === 'color' && defaultMeasureNameColumnId) {
            columnIds.push(defaultMeasureNameColumnId);
        }
        return {
            key: customKey,
            columns: columnIds,
        };
    });
    return [
        {
            key: 'basic',
            dimensions,
        },
    ];
};
export const transformCustomChartConfigToAxisConfig = (customChartConfig, allColumns, customToAxisConfigKeyMapping) => {
    const axisConfig = {
        category: null,
        color: null,
        hidden: null,
        measureValues: null,
        sort: null,
        x: null,
        y: null,
    };
    if (!customChartConfig || customChartConfig.length === 0) {
        return axisConfig;
    }
    const measureValuesColumn = allColumns.find(isMeasureValueColumn);
    axisConfig.measureValues = measureValuesColumn
        ? [measureValuesColumn]
        : null;
    customChartConfig[0].dimensions.forEach(dimension => {
        const [, axisKey] = customToAxisConfigKeyMapping.find(([key]) => key === dimension.key);
        if (axisKey in axisConfig) {
            const dimensionColumns = getColumnsfromCustomChartDimension(dimension);
            const columns = dimensionColumns.map(colId => allColumns.find(col => col.id === colId) || null);
            if (axisConfig[axisKey]) {
                axisConfig[axisKey] = axisConfig[axisKey].concat(columns);
            }
            else {
                axisConfig[axisKey] = columns;
            }
        }
    });
    return axisConfig;
};
export const isMeasureValueQueryColumn = (columnId) => {
    const queries = getCustomChartConfigQueries();
    const column = queries
        .map((query) => query.queryColumns.find(col => col.id === columnId))
        .find(col => col);
    return (column && column?.isMeasureValue) || false;
};
export const getColumnsFromCustomChartConfig = (customChartConfig) => {
    let allColumnsInCustomChartConfig = [];
    if (customChartConfig && customChartConfig.length > 0) {
        customChartConfig.forEach(config => {
            config.dimensions?.forEach(item => {
                const dimensionColumns = getColumnsfromCustomChartDimension(item);
                allColumnsInCustomChartConfig = allColumnsInCustomChartConfig.concat(dimensionColumns);
            });
        });
    }
    return allColumnsInCustomChartConfig;
};
export const transformAxisConfigToCustomChartConfigGeneric = (axisConfig, customToAxisConfigKeyMapping, customAxisConfigToCustomChartConfigTransformer, dimensionType = 'AxisDriven') => {
    const dimensions = [];
    customToAxisConfigKeyMapping.forEach(([customKey, axisKey]) => {
        const columnsForAxis = axisConfig[axisKey] || [];
        const columnIds = getColumnIds(columnsForAxis);
        let config = null;
        if (dimensionType === 'AxisDriven') {
            config = {
                key: customKey,
                __typename: 'AxisDrivenChartConfigDimension',
                axes: columnIds.map(column => ({
                    column: column,
                    __typename: 'FlatAxis',
                })),
            };
        }
        else {
            config = {
                key: customKey,
                columns: columnIds,
                __typename: 'ColumnDrivenChartConfigDimension',
            };
        }
        if (customAxisConfigToCustomChartConfigTransformer) {
            config = customAxisConfigToCustomChartConfigTransformer(customKey, columnIds);
        }
        const currentKeyDimentionIndex = dimensions.findIndex(dim => dim.key === config.key);
        if (currentKeyDimentionIndex !== -1) {
            if (dimensions[currentKeyDimentionIndex].__typename === 'AxisDrivenChartConfigDimension') {
                dimensions[currentKeyDimentionIndex].axes = dimensions[currentKeyDimentionIndex].axes.concat(columnIds.map(column => ({
                    column: column,
                    __typename: 'FlatAxis',
                })));
            }
            else {
                dimensions[currentKeyDimentionIndex].columns = dimensions[currentKeyDimentionIndex].columns.concat(columnIds);
            }
            return;
        }
        dimensions.push(config);
    });
    return [
        {
            key: 'basic',
            dimensions,
            __typename: 'CustomChartConfig',
        },
    ];
};
export const transformCustomChartConfigToAxisConfigGeneric = (customChartConfig, allColumns, customToAxisConfigKeyMapping) => {
    const axisConfig = {
        category: null,
        color: null,
        hidden: null,
        measureValues: null,
        sort: null,
        x: null,
        y: null,
    };
    if (!customChartConfig || customChartConfig.length === 0) {
        return axisConfig;
    }
    customChartConfig[0].dimensions.forEach(dimension => {
        const [, axisKey] = customToAxisConfigKeyMapping.find(([key]) => key === dimension.key);
        if (axisKey in axisConfig) {
            const dimensionColumns = getColumnsfromCustomChartDimension(dimension);
            const columns = dimensionColumns.map(colId => allColumns.find(col => col.id === colId) || null);
            if (axisConfig[axisKey]) {
                axisConfig[axisKey] = axisConfig[axisKey].concat(columns);
            }
            else {
                axisConfig[axisKey] = columns;
            }
        }
    });
    return axisConfig;
};
export const getSortOrderByColId = (viz, columnId) => {
    if (_.isEmpty(viz.sortInfo)) {
        logger.debug('Sort info is missing in the custom chart object');
        if (!_.isEmpty(viz.topInfo)) {
            return _.find(viz.topInfo, sortOrder => sortOrder.columnId === columnId);
        }
        const chartModel = viz;
        if (_.isNil(chartModel.sortOrder)) {
            logger.debug('Sort order is missing in the custom chart object');
            return null;
        }
        return _.find(chartModel.sortOrder, sortOrder => sortOrder.columnId === columnId);
    }
    return _.find(viz.sortInfo, sortOrder => sortOrder.columnId === columnId);
};
//# sourceMappingURL=custom-chart-model.utils.js.map